import {useMemo, useState} from "react";
import {Stack, Typography} from "@mui/material";
import {Navigate, useLocation, useNavigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import ProfileDetailsForm from "./panels/details/ProfileDetailsForm.tsx";
import type {ProfileFieldDefinition, ProfileFieldValues} from "../../../sdo/ProfileFieldDefinition.ts";
import type {SessionIntelProfile} from "../../../sdo/SessionIntelProfile.ts";

// TODO: Replace with API call that returns the IntelDefinition's field schema for typeKey/defKey.
// Mirrors desktop DefinitionFormBuilder.buildFormLayout(intelDefinition) — always prepends the
// required DISPLAY field, then appends definition-specific fields sorted by position.
function buildSampleCreateFields(friendlyName: string): ProfileFieldDefinition[] {
    return [
        {key: "DISPLAY", label: "Display", hintText: `Display name for the ${friendlyName}`, position: 1, dataType: "STRING", required: true},
        {key: "FULL_NAME", label: "Full Name", hintText: "Legal name on file", position: 2, dataType: "STRING", required: false},
        {key: "ALIASES", label: "Known Aliases", hintText: "One per line, comma-separated, or freeform", position: 3, dataType: "TEXT", required: false},
        {key: "DATE_OF_BIRTH", label: "Date of Birth", hintText: "Best known DOB", position: 4, dataType: "DATE", required: false},
        {
            key: "NATIONALITY",
            label: "Nationality",
            hintText: "Primary nationality of record",
            position: 5,
            dataType: "LIST_ENTRY",
            required: false,
            options: [
                {key: "US", label: "United States"},
                {key: "CA", label: "Canada"},
                {key: "UK", label: "United Kingdom"},
                {key: "AU", label: "Australia"},
                {key: "DE", label: "Germany"},
                {key: "FR", label: "France"},
                {key: "JP", label: "Japan"},
                {key: "OTHER", label: "Other"},
            ],
        },
        {
            key: "CONFIDENCE",
            label: "Confidence Level",
            hintText: "Analyst confidence in source attribution",
            position: 6,
            dataType: "LIST_ENTRY",
            required: true,
            options: [
                {key: "LOW", label: "Low"},
                {key: "MEDIUM", label: "Medium"},
                {key: "HIGH", label: "High"},
                {key: "VERIFIED", label: "Verified"},
            ],
        },
        {key: "DESCRIPTION", label: "Description", hintText: "Free-form analyst notes", position: 7, dataType: "TEXT", required: false},
        {key: "ACTIVE", label: "Active Profile", hintText: "Uncheck to mark as inactive", position: 8, dataType: "BOOLEAN", required: false},
    ];
}

function emptyValues(fields: ProfileFieldDefinition[]): ProfileFieldValues {
    const out: ProfileFieldValues = {};
    for (const f of fields) {
        out[f.key] = f.dataType === "BOOLEAN" ? "true" : "";
    }
    return out;
}

function titleCase(raw: string): string {
    if (!raw) return "";
    return raw
        .split(/[._-]+/)
        .map((part) => part.length === 0 ? "" : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(" ")
        .trim();
}

type LocationStateShape = { profile?: SessionIntelProfile & { type?: string } };

function UXIntelCreate() {
    const { networkUID, typeKey, defKey } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const state = (location.state ?? null) as LocationStateShape | null;

    const friendlyName = useMemo(() => {
        if (state?.profile?.name) return state.profile.name;
        return titleCase(String(defKey ?? ""));
    }, [state, defKey]);

    const friendlyType = useMemo(() => titleCase(String(typeKey ?? "")), [typeKey]);

    const fields = useMemo(() => buildSampleCreateFields(friendlyName), [friendlyName]);
    const [values, setValues] = useState<ProfileFieldValues>(() => emptyValues(fields));

    const intelManagerPath = `/networks/${networkUID}/intel/${typeKey}/${defKey}`;

    function handleCancel() {
        navigate(intelManagerPath, { state: { profile: state?.profile } });
    }

    function handleSave(submitted: ProfileFieldValues) {
        // TODO: Replace with real API call — mirror desktop UXIntelCreate.createProfile():
        //   const profile = intelDefinition.create(submitted);
        //   intelManager.create(profile);
        //   ProcessorRequests.createOnboarding(networkUID, profile.createPointer(), {});
        //   networkConnection.getRequestManager().submit(onboardingRequest, APPROVED, "ODI_ADMIN");
        console.log(`[UXIntelCreate] create ${typeKey}/${defKey} profile`, submitted);
        setValues(submitted);
        navigate(intelManagerPath, { state: { profile: state?.profile } });
    }

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%" >
                    <WidgetHeader
                        title={`Create ${friendlyName} Profile`}
                        breadcrumbs={[
                            {display: "Networks", linkTo: `/networks/`, activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Intelligence", linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: friendlyType, linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: friendlyName, linkTo: intelManagerPath, activeView: false},
                            {display: "Create", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack
                        sx={appStyles.sectionBox}
                        spacing={0}
                        width="100%"
                        alignItems="center"
                    >
                        {/* Centered title — mirrors desktop CreateFormOptions.setTitle(...) */}
                        <Typography sx={{...appStyles.panelSectionTitle, padding: "16px 0 12px"}}>
                            {`Create ${friendlyName} Profile`}
                        </Typography>

                        {/* Form */}
                        <Stack direction="column" sx={{width: "100%", alignItems: "stretch", padding: "0 16px 16px"}}>
                            <ProfileDetailsForm
                                fields={fields}
                                values={values}
                                editable={true}
                                onSave={handleSave}
                                onCancel={handleCancel}
                                saveLabel="Create Profile"
                                alwaysEnableSave={true}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXIntelCreate;
